import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Music, Calendar, Headphones, Award, CheckCircle, ArrowRight, Mail, Phone, 
  MapPin, Send, Loader2, Instagram, Facebook, Youtube, Star, Building2, 
  Users, Package, Sparkles, Store, Search, SlidersHorizontal, X
} from 'lucide-react';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { format } from 'date-fns';
import { getErrorMessage } from '../utils/errorHandler';

const API = process.env.REACT_APP_BACKEND_URL || '';

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'General Enquiry',
    message: ''
  });
  const [contactStatus, setContactStatus] = useState({ loading: false, success: false, error: '' });
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDate, setSearchDate] = useState(null);
  const [searchType, setSearchType] = useState('all'); // 'all', 'pa', 'equipment'
  const [searchCategory, setSearchCategory] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [searching, setSearching] = useState(false);
  
  // Platform fee waiver state
  const [feeWaiver, setFeeWaiver] = useState(null);

  useEffect(() => {
    fetchBusinesses();
    fetchPlatformSettings();
  }, []);

  const fetchBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/api/businesses?verified_only=true&active_only=true`);
      setBusinesses(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch businesses:', error);
      setBusinesses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatformSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/platform/settings`);
      if (response.data?.fee_waiver_active) {
        setFeeWaiver(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch platform settings:', error);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactStatus({ loading: true, success: false, error: '' });
    
    try {
      await axios.post(`${API}/api/contact`, contactForm);
      setContactStatus({ loading: false, success: true, error: '' });
      setContactForm({ name: '', email: '', phone: '', subject: 'General Enquiry', message: '' });
    } catch (error) {
      setContactStatus({ 
        loading: false, 
        success: false, 
        error: getErrorMessage(error, 'Failed to send message. Please try again.')
      });
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    setSearching(true);
    
    // Build search params
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (searchDate) params.set('date', format(searchDate, 'yyyy-MM-dd'));
    if (searchCategory !== 'all') params.set('category', searchCategory);
    
    // Navigate based on type
    if (searchType === 'equipment') {
      navigate(`/equipment?${params.toString()}`);
    } else if (searchType === 'pa') {
      navigate(`/browse?${params.toString()}`);
    } else {
      // For 'all', navigate to browse with search params
      navigate(`/browse?${params.toString()}`);
    }
    setSearching(false);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSearchDate(null);
    setSearchType('all');
    setSearchCategory('all');
  };

  const hasActiveFilters = searchDate || searchType !== 'all' || searchCategory !== 'all';

  const renderStars = (rating) => {
    if (!rating) return null;
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="text-sm text-gray-600 ml-1">({rating})</span>
      </div>
    );
  };

  const features = [
    {
      icon: Store,
      title: 'Multiple Vendors',
      description: 'Choose from various verified rental businesses'
    },
    {
      icon: Calendar,
      title: 'Easy Booking',
      description: 'Check availability and book in minutes'
    },
    {
      icon: Headphones,
      title: 'Quality Equipment',
      description: 'Professional-grade PA systems and gear'
    },
    {
      icon: Award,
      title: 'Verified Businesses',
      description: 'All vendors are verified for your peace of mind'
    }
  ];

  const benefits = [
    'Browse multiple rental businesses',
    'Compare prices and equipment',
    'Read verified customer reviews',
    'Secure online booking',
    'Professional-grade equipment',
    'Flexible rental periods'
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Fee Waiver Banner */}
      {feeWaiver && feeWaiver.fee_waiver_active && (
        <div className="bg-gradient-to-r from-green-600 to-green-500 text-white py-3 px-4 text-center" data-testid="fee-waiver-banner">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 flex-wrap">
            <Sparkles className="h-5 w-5" />
            <span className="font-semibold">
              Platform charges do not apply until{' '}
              {feeWaiver.fee_waiver_end_date && new Date(feeWaiver.fee_waiver_end_date).toLocaleDateString('en-ZA', { 
                month: 'long', 
                year: 'numeric' 
              })}
            </span>
            <span className="text-green-100">
              — Onboard your business now with 0% platform fee!
            </span>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden min-h-[600px] flex items-center">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1920&q=80" 
            alt="Concert background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50" />
        </div>
        <div className="max-w-7xl mx-auto relative w-full">
          <div className="text-center space-y-6">
            {/* Logo */}
            <div className="flex justify-center mb-2">
              <img 
                src="/logo.png" 
                alt="Gumba Logo" 
                className="h-32 sm:h-40 object-contain drop-shadow-2xl"
              />
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold -mt-2">
              <span className="text-white drop-shadow-lg">
                Gumba
              </span>
            </h1>
            {/* Slogan */}
            <p className="text-2xl sm:text-3xl font-semibold text-yellow-500 tracking-wide">
              Professional Sound Rentals Marketplace
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              South Africa's premier marketplace for PA system and audio equipment rentals. 
              Connect with verified vendors and find the perfect sound solution for your event.
            </p>
            
            {/* Google-Style Search Bar */}
            <div className="max-w-3xl mx-auto pt-4">
              <form onSubmit={handleSearch} className="relative">
                <div className="flex flex-col gap-3">
                  {/* Main Search Input */}
                  <div className="relative flex items-center bg-white rounded-full shadow-xl overflow-hidden">
                    <Search className="absolute left-4 h-5 w-5 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search PA systems, equipment, or vendors..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-12 pr-32 py-6 text-lg border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-gray-900 placeholder:text-gray-500 rounded-full"
                      data-testid="hero-search-input"
                    />
                    <div className="absolute right-2 flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={`rounded-full ${hasActiveFilters ? 'text-yellow-600 bg-yellow-50' : 'text-gray-500'}`}
                        onClick={() => setShowFilters(!showFilters)}
                        data-testid="search-filters-btn"
                      >
                        <SlidersHorizontal className="h-4 w-4 mr-1" />
                        Filters
                        {hasActiveFilters && <span className="ml-1 w-2 h-2 bg-yellow-500 rounded-full" />}
                      </Button>
                      <Button
                        type="submit"
                        className="bg-yellow-500 hover:bg-yellow-600 text-black rounded-full px-6"
                        disabled={searching}
                        data-testid="hero-search-btn"
                      >
                        {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Filter Options */}
                  {showFilters && (
                    <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-xl animate-in slide-in-from-top-2 duration-200">
                      <div className="flex flex-wrap gap-4 items-end">
                        {/* Type Filter */}
                        <div className="flex-1 min-w-[140px]">
                          <Label className="text-gray-700 text-sm font-medium mb-1 block">Type</Label>
                          <Select value={searchType} onValueChange={setSearchType}>
                            <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                              <SelectValue placeholder="All Types" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200">
                              <SelectItem value="all" className="text-gray-900">All Types</SelectItem>
                              <SelectItem value="pa" className="text-gray-900">PA Systems</SelectItem>
                              <SelectItem value="equipment" className="text-gray-900">Equipment</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Category Filter */}
                        <div className="flex-1 min-w-[140px]">
                          <Label className="text-gray-700 text-sm font-medium mb-1 block">Category</Label>
                          <Select value={searchCategory} onValueChange={setSearchCategory}>
                            <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                              <SelectValue placeholder="All Categories" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200">
                              <SelectItem value="all" className="text-gray-900">All Categories</SelectItem>
                              <SelectItem value="Basic" className="text-gray-900">Basic</SelectItem>
                              <SelectItem value="Premium" className="text-gray-900">Premium</SelectItem>
                              <SelectItem value="Professional" className="text-gray-900">Professional</SelectItem>
                              <SelectItem value="Conference" className="text-gray-900">Conference</SelectItem>
                              <SelectItem value="Festival" className="text-gray-900">Festival</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Date Filter */}
                        <div className="flex-1 min-w-[160px]">
                          <Label className="text-gray-700 text-sm font-medium mb-1 block">Event Date</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className={`w-full justify-start text-left font-normal bg-gray-50 border-gray-200 text-gray-900 hover:bg-gray-100 ${!searchDate && 'text-gray-500'}`}
                              >
                                <Calendar className="mr-2 h-4 w-4" />
                                {searchDate ? format(searchDate, 'PPP') : 'Pick a date'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-gray-800 border-gray-700" align="start">
                              <CalendarComponent
                                mode="single"
                                selected={searchDate}
                                onSelect={setSearchDate}
                                disabled={(date) => date < new Date()}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        
                        {/* Clear Filters */}
                        {hasActiveFilters && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={clearFilters}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </form>
              
              {/* Quick Filter Pills */}
              <div className="flex flex-wrap gap-2 justify-center mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full"
                  onClick={() => { setSearchType('pa'); setSearchCategory('Premium'); handleSearch(); }}
                >
                  <Music className="h-3 w-3 mr-1" /> Premium PA
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full"
                  onClick={() => { setSearchType('equipment'); handleSearch(); }}
                >
                  <Headphones className="h-3 w-3 mr-1" /> Equipment
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full"
                  onClick={() => { setSearchType('pa'); setSearchCategory('Festival'); handleSearch(); }}
                >
                  <Sparkles className="h-3 w-3 mr-1" /> Festival Setup
                </Button>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button
                size="lg"
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-lg px-8 py-6 transition-all shadow-lg hover:shadow-xl text-black font-semibold"
                onClick={() => document.getElementById('vendors')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="browse-vendors-btn"
              >
                <Store className="mr-2 h-5 w-5" />
                Browse Vendors
              </Button>
              <Button
                size="lg"
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-lg px-8 py-6 transition-all shadow-lg hover:shadow-xl text-white"
                onClick={() => navigate('/become-vendor')}
                data-testid="list-business-btn"
              >
                <Building2 className="mr-2 h-5 w-5" />
                List Your Business
              </Button>
            </div>
            {!user && (
              <div className="pt-4 flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  variant="outline"
                  className="text-lg px-8 py-6 border-2 border-white text-white hover:bg-white hover:text-gray-900 transition-all"
                  onClick={() => navigate('/login')}
                  data-testid="get-started-btn"
                >
                  Sign In
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-lg px-8 py-6 border-2 border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black transition-all"
                  onClick={() => navigate('/register')}
                  data-testid="register-btn"
                >
                  Create Account
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Why Choose Our Marketplace?</h2>
            <p className="text-xl text-gray-400">The smarter way to rent audio equipment</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="bg-gray-700 border-gray-600 hover:border-yellow-500 transition-all">
                <CardContent className="pt-6 text-center">
                  <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="h-8 w-8 text-yellow-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Vendors Section */}
      <section id="vendors" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              <Sparkles className="inline-block h-8 w-8 text-yellow-500 mr-2" />
              Featured Vendors
            </h2>
            <p className="text-xl text-gray-400">Verified businesses ready to serve your events</p>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-yellow-500" />
            </div>
          ) : businesses.length === 0 ? (
            <div className="text-center py-12">
              <Store className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No vendors available yet.</p>
              <p className="text-gray-500">Be the first to list your business!</p>
              <Button 
                className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-black"
                onClick={() => navigate('/become-vendor')}
              >
                Register Your Business
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {businesses.map((business) => (
                <Card 
                  key={business.business_id} 
                  className="bg-gray-800 border-gray-700 hover:border-yellow-500 transition-all cursor-pointer overflow-hidden group"
                  onClick={() => navigate(`/business/${business.business_id}`)}
                  data-testid={`business-card-${business.business_id}`}
                >
                  {/* Cover/Logo Section */}
                  <div className="relative h-48 bg-gradient-to-br from-gray-700 to-gray-800">
                    {business.cover_image_url ? (
                      <img 
                        src={business.cover_image_url.startsWith('/') ? `${API}${business.cover_image_url}` : business.cover_image_url}
                        alt={business.business_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music className="h-16 w-16 text-gray-600" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent" />
                    
                    {/* Logo */}
                    <div className="absolute bottom-4 left-4 flex items-end gap-3">
                      <div className="w-16 h-16 rounded-full bg-gray-700 border-2 border-yellow-500 overflow-hidden flex-shrink-0">
                        {business.logo_url ? (
                          <img 
                            src={business.logo_url.startsWith('/') ? `${API}${business.logo_url}` : business.logo_url}
                            alt={`${business.business_name} logo`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Building2 className="h-8 w-8 text-gray-500" />
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white group-hover:text-yellow-500 transition-colors">
                          {business.business_name}
                        </h3>
                        {business.average_rating && renderStars(business.average_rating)}
                      </div>
                    </div>
                    
                    {/* Verified Badge */}
                    <Badge className="absolute top-4 right-4 bg-green-600 text-white">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Verified
                    </Badge>
                  </div>
                  
                  <CardContent className="pt-4">
                    <p className="text-gray-400 text-sm line-clamp-2 mb-4">
                      {business.description}
                    </p>
                    
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
                      <MapPin className="h-4 w-4" />
                      <span>{business.address?.city}, {business.address?.province}</span>
                    </div>
                    
                    {business.total_bookings > 0 && (
                      <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <Users className="h-4 w-4" />
                        <span>{business.total_bookings} bookings completed</span>
                      </div>
                    )}
                    
                    <Button 
                      className="w-full mt-4 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/business/${business.business_id}`);
                      }}
                    >
                      View Equipment
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">The Smart Way to Rent Audio Equipment</h2>
              <p className="text-xl text-gray-400 mb-8">
                Join hundreds of event organizers who trust our marketplace for their audio needs.
              </p>
              <div className="grid grid-cols-1 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-6 w-6 text-yellow-500 flex-shrink-0" />
                    <span className="text-gray-300">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gray-700 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Are you a vendor?</h3>
              <p className="text-gray-400 mb-6">
                List your PA systems and equipment on our marketplace. Reach more customers 
                and grow your rental business.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-gray-300">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Free business listing
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Manage bookings easily
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Build your reputation with reviews
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Only 10% platform fee
                </li>
              </ul>
              <Button 
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-6"
                onClick={() => navigate('/become-vendor')}
              >
                <Building2 className="mr-2 h-5 w-5" />
                Register Your Business
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Contact Us</h2>
            <p className="text-xl text-gray-400">Have questions? We're here to help!</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-8">
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-yellow-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Address</h3>
                      <p className="text-gray-400">3421 Themba Drive, Hillsview</p>
                      <p className="text-gray-400">Kagiso 2, 1754</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-yellow-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Phone</h3>
                      <p className="text-gray-400">065 910 4992</p>
                      <p className="text-gray-400">076 390 1308</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-yellow-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Email</h3>
                      <p className="text-gray-400">platform@byalani.co.za</p>
                      <p className="text-gray-400">support@byalani.co.za</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <div className="flex gap-4 justify-center">
                <a href="https://instagram.com/byalanievents" target="_blank" rel="noopener noreferrer" 
                   className="w-12 h-12 bg-gray-700 hover:bg-yellow-500 rounded-full flex items-center justify-center transition-colors group">
                  <Instagram className="h-6 w-6 text-gray-400 group-hover:text-black" />
                </a>
                <a href="https://facebook.com/byalanievents" target="_blank" rel="noopener noreferrer"
                   className="w-12 h-12 bg-gray-700 hover:bg-yellow-500 rounded-full flex items-center justify-center transition-colors group">
                  <Facebook className="h-6 w-6 text-gray-400 group-hover:text-black" />
                </a>
              </div>
            </div>
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Send us a message</CardTitle>
                <CardDescription className="text-gray-400">We'll get back to you within 24 hours</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContactSubmit} className="space-y-4" data-testid="contact-form">
                  <div>
                    <Label htmlFor="name" className="text-gray-300">Name</Label>
                    <Input
                      id="name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      required
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="Your name"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-gray-300">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      required
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="your@email.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-gray-300">Phone (optional)</Label>
                    <Input
                      id="phone"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="Your phone number"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject" className="text-gray-300">Subject</Label>
                    <Select
                      value={contactForm.subject}
                      onValueChange={(value) => setContactForm({...contactForm, subject: value})}
                    >
                      <SelectTrigger className="bg-gray-700 border-gray-600 text-white" data-testid="contact-subject-select">
                        <SelectValue placeholder="Select subject" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        <SelectItem value="General Enquiry">General Enquiry</SelectItem>
                        <SelectItem value="Booking Question">Booking Question</SelectItem>
                        <SelectItem value="Business Listing">Business Listing</SelectItem>
                        <SelectItem value="Technical Support">Technical Support</SelectItem>
                        <SelectItem value="Feedback">Feedback</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-gray-300">Message</Label>
                    <Textarea
                      id="message"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      required
                      rows={4}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="How can we help you?"
                      data-testid="contact-message-input"
                    />
                  </div>
                  {contactStatus.error && (
                    <p className="text-red-400 text-sm" data-testid="contact-error">{contactStatus.error}</p>
                  )}
                  {contactStatus.success && (
                    <p className="text-green-400 text-sm" data-testid="contact-success">Message sent successfully!</p>
                  )}
                  <Button 
                    type="submit" 
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                    disabled={contactStatus.loading}
                    data-testid="contact-send-btn"
                  >
                    {contactStatus.loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center gap-8">
            {/* Main Footer Content */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 w-full">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center">
                  <Music className="h-6 w-6 text-black" />
                </div>
                <div>
                  <p className="text-white font-bold text-lg">Gumba Marketplace</p>
                  <p className="text-gray-500 text-sm">Professional Sound Rentals</p>
                </div>
              </div>
              
              {/* Quick Links */}
              <div className="flex items-center gap-6 text-sm">
                <a data-testid="footer-terms-link" href="/api/policies/terms" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-yellow-500 transition-colors">
                  Terms
                </a>
                <a data-testid="footer-privacy-link" href="/api/policies/privacy" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-yellow-500 transition-colors">
                  Privacy
                </a>
                <a data-testid="footer-popia-link" href="/api/policies/popia" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-yellow-500 transition-colors">
                  POPIA
                </a>
              </div>
            </div>
            
            {/* PDF Downloads */}
            <div className="flex items-center gap-4 text-xs">
              <span className="text-gray-400">Download:</span>
              <a 
                href="/api/policies/terms/pdf" 
                data-testid="footer-terms-pdf-link"
                download 
                className="text-gray-400 hover:text-yellow-500 transition-colors flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Terms PDF
              </a>
              <a 
                href="/api/policies/privacy/pdf" 
                data-testid="footer-privacy-pdf-link"
                download 
                className="text-gray-400 hover:text-yellow-500 transition-colors flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Privacy PDF
              </a>
              <a 
                href="/api/policies/popia/pdf" 
                data-testid="footer-popia-pdf-link"
                download 
                className="text-gray-400 hover:text-yellow-500 transition-colors flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                POPIA PDF
              </a>
            </div>
            
            {/* Divider */}
            <div className="w-full border-t border-gray-800"></div>
            
            {/* Powered by Byalani */}
            <div className="flex flex-col items-center gap-4">
              <p className="text-gray-400 text-sm flex items-center gap-2">
                Powered by{' '}
                <a 
                  href="https://byalani.co.za" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-yellow-500 hover:text-yellow-400 font-semibold transition-colors inline-flex items-center gap-1.5"
                >
                  <img 
                    src="/byalani-logo.png" 
                    alt="Byalani" 
                    className="h-5 w-5 object-contain inline-block"
                  />
                  Byalani
                </a>
              </p>
              <p className="text-gray-600 text-xs">
                © {new Date().getFullYear()} Gumba Marketplace. All rights reserved.
              </p>
              <p className="text-gray-700 text-xs">
                South Africa&apos;s Premier Sound Rental Platform
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
